import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    ACTIVE, INACTIVE,
    PUBLIC, NON_PUBLIC, PRIVATE,
    EDIT,
} from "const"
import { mtPackagesRoute } from "client/parts/routes"
import { MtPackage, MtPackageCreate, MtPackageAllowed } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    PackageValidation,
    SavePackage,
    SaveAllowedPackages,
    closeModal,
    setPackageEdit,
    setPackageAllowed,
    setPackageReloadList,
    setPackageReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    RadioButton,
    AccountDropdown,
    AccountDropdownList,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditPackageProps {
    packg: MtPackage
}

let isMounted: boolean

const Form = ({
    packg,
}: EditPackageProps) => {
    const settings = useSelector((state: StoreType) => state.package.packageSettings)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(packg.name)
    const [price, setPrice] = useInput(packg.price !== null ? Number(packg.price).toFixed(2) : "")

    const [maxUsers, setMaxUsers] = useState(packg.maxUsers || 0)
    const [status, setStatus] = useState(packg.status)
    const [visibility, setVisibility] = useState(packg.visibility)
    const [allowedAccounts, setAllowedAccounts] = useState<number[]>([])

    const [validation, setValidation] = useState<PackageValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        forceRerenderContent(mtPackagesRoute, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const priceParsed = Math.round(parseFloat(price) * 100) / 100

        const newPackageData: MtPackageCreate = {
            name,
            settings: JSON.stringify(settings),
            maxUsers,
            price: priceParsed > 0 ? priceParsed : 0,
            status,
            visibility,
        }

        SavePackage(packg.id, newPackageData, lang)
        .then((res) => {
            if (res.validation) {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
                return
            }

            if (res.newPackageId) replaceHistoryState(`${mtPackagesRoute}/${EDIT}/${res.newPackageId}`)
            const packageId = res.newPackageId || packg.id

            const allowedPackages: MtPackageAllowed[] = []
            allowedAccounts.forEach((accountId) => {
                allowedPackages.push({
                    accountId,
                    packageId,
                })
            })

            SaveAllowedPackages(packageId, allowedPackages)
            .then((updatedAllowedAccounts) => {
                dispatch(setPackageEdit({
                    id: packageId,
                    ...newPackageData,
                }))
                dispatch(setPackageAllowed({ [packageId]: updatedAllowedAccounts }))

                dispatch(setPackageReloadList(false))
                setTimeout(() => {
                    dispatch(setPackageReloadList(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setPackageReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setPackageReloadEdit(true))
                    }, 0)
                }
            })
        })
    }

    const disableSave = (visibility === NON_PUBLIC || visibility === PRIVATE) && !allowedAccounts.length

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.packageMaxUsers}</Label>
                        <Input
                            type="text"
                            value={maxUsers}
                            onChange={(e) => setMaxUsers(parseInt(e.target.value, 10) || 0)}
                        />
                        <Feedback>{validation && validation.maxUsers}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.price}</Label>
                        <Input type="text" value={price} onChange={setPrice} />
                        <Feedback>{validation && validation.price}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={status === ACTIVE}
                                onChange={() => setStatus(ACTIVE)}
                                title={lang.active}
                            />
                            <RadioButton
                                checked={status === INACTIVE}
                                onChange={() => setStatus(INACTIVE)}
                                title={lang.inactive}
                            />
                        </FormGroup>
                        <Feedback>{validation && validation.status}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.visibility}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={visibility === PUBLIC}
                                onChange={() => {
                                    setVisibility(PUBLIC)
                                    setAllowedAccounts([])
                                }}
                                title={lang.public}
                            />
                            <RadioButton
                                checked={visibility === NON_PUBLIC}
                                onChange={() => {
                                    setVisibility(NON_PUBLIC)
                                    setAllowedAccounts([])
                                }}
                                title={lang.nonPublic}
                            />
                            <RadioButton
                                checked={visibility === PRIVATE}
                                onChange={() => {
                                    setVisibility(PRIVATE)
                                    setAllowedAccounts([])
                                }}
                                title={lang.private}
                            />
                        </FormGroup>
                        <Feedback>{validation && validation.visibility}</Feedback>
                    </FormGroup>

                    {
                        visibility === NON_PUBLIC && (
                            <FormGroup>
                                <Label>{lang.accounts}</Label>
                                <AccountDropdownList
                                    selectedIds={allowedAccounts}
                                    title={`+ ${lang.addAccount}`}
                                    onChange={setAllowedAccounts}
                                />
                                <Feedback>{!allowedAccounts.length && lang.validationFieldRequired}</Feedback>
                            </FormGroup>
                        )
                    }
                    {
                        visibility === PRIVATE && (
                            <FormGroup>
                                <Label>{lang.account}</Label>
                                <AccountDropdown
                                    selectedId={allowedAccounts.length ? allowedAccounts[0] : null}
                                    title={lang.selectAccount}
                                    showDelete={false}
                                    onChange={(accountId) => setAllowedAccounts([accountId])}
                                />
                                <Feedback>{!allowedAccounts.length && lang.validationFieldRequired}</Feedback>
                            </FormGroup>
                        )
                    }
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        disabled={disableSave}
                        onClick={() => onSave(1)}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button
                        variant="success"
                        row
                        disabled={disableSave}
                        onClick={() => onSave(2)}
                    >
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
